import { useQuery } from '@tanstack/react-query';
import { PlusIcon } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import type { CarrierWeightBracketDTO } from '@invenco/shipping-interface';

import { Badge } from '@/components/core/badge';
import { Button } from '@/components/core/button';
import { Card, CardHeader, CardTitle } from '@/components/core/card';
import { DialogTrigger } from '@/components/core/dialog';
import { Skeleton } from '@/components/core/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/core/tabs';
import { FormDialog } from '@/components/forms/form-dialog';
import { useGateways } from '@/gateways/GatewayProvider';
import { cn, fetchAllPages } from '@/shared/helpers';

import { AddCarrierServiceDialogContent } from './add-carrier-service-dialog-content';
import { CarrierWeightBracketTable } from './carrier-weight-bracket-table';
import type { CarrierAccountData } from './use-carrier-account';

type BracketTab = {
  /** Tab value: 'default' for the account-level config, otherwise `service-<carrierServiceId>`. */
  value: string;
  label: string;
  /** null for the account-level default, otherwise the service this config applies to. */
  carrierServiceId: string | null;
  config: CarrierWeightBracketDTO['bracketsConfig'];
  /** Existing config id; undefined for an unsaved draft, so its first save creates the config. */
  bracketId?: string;
};

// Tab value for a config. Service values are prefixed so a service whose id is literally 'default'
// can't collide with the account-level default tab.
const tabValue = (carrierServiceId: string | null) =>
  carrierServiceId === null ? 'default' : `service-${carrierServiceId}`;

type Props = {
  /** The account's carrier — scopes the addable services. */
  carrierId?: string;
  weightBrackets: CarrierAccountData['weightBrackets'];
  isLoading: boolean;
  saveBrackets: CarrierAccountData['saveBrackets'];
};

/**
 * The weight-bracket configs for a carrier account, one inline editable table per tab — the
 * account-level default plus one per configured service. Adding a service opens a blank draft tab
 * whose first save creates the config (persisted straight into the cache by `saveBrackets`).
 */
export function WeightBracketsCard({ carrierId, weightBrackets, isLoading, saveBrackets }: Props) {
  const { shippingGateway } = useGateways();
  const { data: carrierServices = [] } = useQuery({
    queryKey: ['carrierServices'],
    queryFn: ({ signal }) =>
      fetchAllPages(({ cursor, take }) =>
        shippingGateway.getCarrierServices({ cursor, take }, { signal }),
      ),
  });
  const serviceNameById = useMemo(
    () => new Map(carrierServices.map((service) => [service.id, service.name])),
    [carrierServices],
  );

  const accountBracket = weightBrackets?.find((bracket) => bracket.carrierServiceId === null);
  const serviceBrackets = useMemo(
    () =>
      (weightBrackets ?? [])
        .filter(
          (bracket): bracket is CarrierWeightBracketDTO & { carrierServiceId: string } =>
            bracket.carrierServiceId !== null,
        )
        .sort((a, b) =>
          (serviceNameById.get(a.carrierServiceId) ?? a.carrierServiceId).localeCompare(
            serviceNameById.get(b.carrierServiceId) ?? b.carrierServiceId,
          ),
        ),
    [weightBrackets, serviceNameById],
  );

  // A pending config is one the user has added but not yet saved: a tab with a blank draft table and
  // no backend record. Adding via the "+" picker starts a new draft, which replaces any prior unsaved
  // one (only one draft at a time), and the active tab moving away abandons it.
  const [pending, setPending] = useState<{ carrierServiceId: string | null } | null>(null);
  const [selectedTab, setSelectedTab] = useState<string>();

  const persistedTabs: BracketTab[] = [
    ...(accountBracket
      ? [
          {
            value: tabValue(null),
            label: 'Default (all services)',
            carrierServiceId: null,
            config: accountBracket.bracketsConfig,
            bracketId: accountBracket.id,
          },
        ]
      : []),
    ...serviceBrackets.map((bracket) => ({
      value: tabValue(bracket.carrierServiceId),
      label: serviceNameById.get(bracket.carrierServiceId) ?? bracket.carrierServiceId,
      carrierServiceId: bracket.carrierServiceId,
      config: bracket.bracketsConfig,
      bracketId: bracket.id,
    })),
  ];

  // Once a draft's first save lands in the cache, the persisted config supersedes it (same tab
  // value, now with a real id) — drop the now-redundant draft.
  const isPendingPersisted =
    pending != null &&
    persistedTabs.some((tab) => tab.carrierServiceId === pending.carrierServiceId);
  useEffect(() => {
    if (isPendingPersisted) setPending(null);
  }, [isPendingPersisted]);

  const pendingTab: BracketTab | null =
    pending && !isPendingPersisted
      ? {
          value: tabValue(pending.carrierServiceId),
          label:
            pending.carrierServiceId === null
              ? 'Default (all services)'
              : (serviceNameById.get(pending.carrierServiceId) ?? pending.carrierServiceId),
          carrierServiceId: pending.carrierServiceId,
          config: [],
          bracketId: undefined,
        }
      : null;

  const tabs = pendingTab ? [...persistedTabs, pendingTab] : persistedTabs;
  const activeTab =
    selectedTab && tabs.some((tab) => tab.value === selectedTab) ? selectedTab : tabs[0]?.value;

  const excludeServiceIds = new Set<string>([
    ...serviceBrackets.map((bracket) => bracket.carrierServiceId),
    ...(pending?.carrierServiceId ? [pending.carrierServiceId] : []),
  ]);
  const canAddDefault = !accountBracket && pendingTab?.carrierServiceId !== null;

  const startDraft = (carrierServiceId: string | null) => {
    setPending({ carrierServiceId });
    setSelectedTab(tabValue(carrierServiceId));
  };

  const handleTabChange = (value: string) => {
    // Leaving an unsaved draft tab abandons it (it was never persisted).
    if (pendingTab && value !== pendingTab.value) setPending(null);
    setSelectedTab(value);
  };

  if (isLoading) return <Skeleton className="h-40 w-full" />;

  const addDialog = (
    <FormDialog>
      <DialogTrigger asChild>
        <Button type="button" variant="ghost" size="icon-sm" aria-label="Add carrier service">
          <PlusIcon />
        </Button>
      </DialogTrigger>
      <AddCarrierServiceDialogContent
        carrierId={carrierId}
        excludeServiceIds={excludeServiceIds}
        canAddDefault={canAddDefault}
        onSelect={startDraft}
      />
    </FormDialog>
  );

  if (tabs.length === 0) {
    return (
      <div className="flex flex-col items-start gap-3 rounded-lg border border-dashed p-6">
        <p className="text-sm text-muted-foreground">
          No weight brackets are configured for this account yet.
        </p>
        {addDialog}
      </div>
    );
  }

  return (
    <Card aria-label="Weight brackets">
      <CardHeader>
        <CardTitle className={cn('flex items-center gap-2')}>
          <span>Weight brackets</span>
          <Badge variant="secondary" aria-label={`with ${weightBrackets?.length ?? 0} brackets`}>
            {weightBrackets?.length ?? 0}
          </Badge>
        </CardTitle>
      </CardHeader>
      <Tabs value={activeTab} onValueChange={handleTabChange} className="gap-0">
        <TabsList
          aria-label="Weight brackets by carrier service"
          variant="button"
          className="px-2 pt-3"
        >
          {tabs.map((tab) => (
            <TabsTrigger key={tab.value} value={tab.value}>
              {tab.label}
            </TabsTrigger>
          ))}
          {addDialog}
        </TabsList>
        {tabs.map((tab) => (
          <TabsContent key={tab.value} value={tab.value} className="mt-0">
            <CarrierWeightBracketTable
              config={tab.config}
              onSave={(bracketsConfig) =>
                saveBrackets({
                  carrierServiceId: tab.carrierServiceId,
                  bracketId: tab.bracketId,
                  bracketsConfig,
                })
              }
            />
          </TabsContent>
        ))}
      </Tabs>
    </Card>
  );
}
